import React, {createContext, ReactNode, useContext, useEffect, useRef, useState} from 'react';
import { getEventSource, closeEventSource } from '../queries/sseClient'
import {jobSSE, PlatformEvent} from "../types/jobDataTypes";
import {Event} from "../types/eventTypes";
import {getEventById} from "../queries/matchPageQueries";


interface SSEProviderProps {
    jobsRunning: jobSSE[];
    openSSE: () => void
}

const url = process.env.REACT_APP_BASE_URL + `/sse/jobsSSE`

const SSEProviderContext = createContext<SSEProviderProps | undefined>(undefined);

 const SSEProv: React.FC<{children: React.ReactNode }> = ({children }) => {
     const eventSourceRef = useRef<EventSource | null>(null);


     const [jobsRunning, setJobsRunning] = useState<jobSSE[]>([]);

     const events = new Map<string, PlatformEvent>();
     const pendingRequests = new Map<string, Promise<any>>();


     function removeJobById (idJob: string): boolean {

         return true

     }


     function addEvent (event: PlatformEvent) {
         events.set(event.id, event);
     }

     function getEvent (id: string) {
         return events.get(id);
     }


     function removeEvent (id: string){
         events.delete(id);
     }

     function getEvents () {
         return events
     }

     const fetchWithDedup = async <T = any>(eventId: string): Promise<T> => {

         // 1️⃣ controlla se esiste già una richiesta in corso con quella chiave
         const lastPromise = pendingRequests.get(eventId);
         if (lastPromise) {
             return lastPromise; // restituisce la stessa Promise
         }

         // 2️⃣ altrimenti crea una nuova richiesta axios
         const promise = getEventById(eventId)
             .then((res) => res as T)
             .finally(() => {
                 // quando la richiesta finisce, la togliamo dalla mappa
                 pendingRequests.delete(eventId);
             });

         // 3️⃣ memorizza la promise in pendingRequests
         pendingRequests.set(eventId,promise);

         // 4️⃣ restituisce la promise al chiamante
         return promise;
     };



     function openSSE(){

         const token = localStorage.getItem("token");

         if (!token) {
             // se non loggato, chiudi e non connettere
             closeEventSource();
             eventSourceRef.current = null;
             return;
         }

         const es = getEventSource(url);
         eventSourceRef.current = es.eventSource;

         if (!es) return;

         // handler INIT
         const handleInit = (event: MessageEvent) => {
             const data = JSON.parse(event.data);

             if (data.type !== "init") return;

             const runningJobs = data.payload.filter((item: any) => item.status === "Running" && (item.type === 'highlight-on-demand' || item.type === 're-encoding' || item.type ==='editing'));

             // aggiungi eventuali eventi collegati
             runningJobs.forEach((job: any) => {
                 if (job.events?.length > 0) {
                     addEvent(job.events[0]);
                 }
             });

             setJobsRunning(runningJobs);
         };

         // handler UPDATE
         const handleUpdate = async (event: MessageEvent) => {
             const data = JSON.parse(event.data);

             if (
                 data.type === "/update/highlight-on-demand" ||
                 data.type === "/update/re-encoding" ||
                 data.type === "/update/editing"
             ) {
                 const update = JSON.parse(data.payload);
                 const updatedJob = update.entities.job;

                 // copia dello stato attuale
                 setJobsRunning((prevJobs) => {
                     const jobs = [...prevJobs];

                     // se il job ha eventi ma non li abbiamo già, li recuperiamo
                     if (updatedJob.events.length > 0) {
                         const eventId = updatedJob.events[0].id;

                         const jobEvent = getEvent(eventId)
                         if (!jobEvent) {

                             fetchWithDedup(eventId).then((response) => {
                                 const event: PlatformEvent = response

                                 updatedJob.events = [event];
                                 addEvent(event)
                                 // aggiorna anche lo stato con l'evento recuperato
                                 setJobsRunning((jobs) =>
                                     jobs.map((j) => (j.id === updatedJob.id ? updatedJob : j))
                                 );
                             });
                         }else{
                             updatedJob.events = [jobEvent]
                         }


                     }

                     // aggiorna o aggiunge il job
                     const idx = jobs.findIndex((j) => j.id === updatedJob.id);
                     if (updatedJob.status === "Running") {
                         if (idx >= 0) {
                             jobs[idx] = updatedJob;
                         } else {
                             jobs.push(updatedJob);
                         }
                     } else {
                         // se non più running, lo rimuovo dalla lista
                         if (idx >= 0) jobs.splice(idx, 1);
                     }

                     return jobs;
                 });
             }
         };

         // handler ERROR
         const handleError = (err: any) => {
             console.error("Errore SSE:", err);
         };

         // attach listeners
         if (es.eventSource !== null && !es.isConnect) {
             es.eventSource.addEventListener("message", handleInit);
             es.eventSource.addEventListener("message", handleUpdate);
             es.eventSource.addEventListener("error", handleError);
         }

         // cleanup
         return () => {
             if (es.eventSource !== null){
             es.eventSource.removeEventListener("message", handleInit);
             es.eventSource.removeEventListener("message", handleUpdate);
             es.eventSource.removeEventListener("error", handleError);
             }
             closeEventSource();
         };
     }

// chiusura connessione quando aggiorni/chiudi la pagina
     useEffect(() => {
         const handleUnload = () => {
             closeEventSource();
         };

         window.addEventListener("beforeunload", handleUnload);
         return () => {
             window.removeEventListener("beforeunload", handleUnload);
         };
     }, []);


     return (
         <SSEProviderContext.Provider value={{jobsRunning, openSSE}}>
             {children}
         </SSEProviderContext.Provider>
     );

 };

// Custom hook to use the filter context
const useSSEProvider = () => {
    const context = useContext(SSEProviderContext);
    if (context === undefined) {
        throw new Error('SSEProvider must be used within a SSE provider');
    }
    return context;
};

export { SSEProv, useSSEProvider };